// client/src/components/labEngine/NotebookCell.jsx

import React, { useRef, useState, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { usePyodide } from './PyodideContext';
import { Play, Trash2, Plus, GripVertical } from 'lucide-react';
import CustomAlert from '../customs/customAlert';
import { validatePinInputs } from '../../utilities/validations/notebookCodeValidation';
import showNotification from '../../utilities/showNotification';
import './notebookCell.css';

const CATEGORY_TAGS = {
  data:          { bg: '#E6F1FB', color: '#185FA5', label: 'data' },
  preprocessing: { bg: '#E1F5EE', color: '#0F6E56', label: 'preprocess' },
  model:         { bg: '#EEEDFE', color: '#534AB7', label: 'model' },
  training:      { bg: '#FAECE7', color: '#993C1D', label: 'train' },
  evaluation:    { bg: '#FAEEDA', color: '#854F0B', label: 'eval' },
  output:        { bg: '#EAF3DE', color: '#3B6D11', label: 'output' },
};

const NotebookCell = ({
  id,
  index,
  category = '',
  initialCode = '',
  simOutputs = [],
  tag = null,
  label = '',
  componentId = '',
  savedCode = '',
  onSaveCell,
  onRegenerateCell,
  onDelete,
  onInsertBelow,
  onCodeChange,
  isLast,
  onCellOutput,
  onEditorMount,
}) => {
  const { runCode, isLoading: pyLoading, pyodide } = usePyodide();

  const [output,    setOutput]    = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runCount,  setRunCount]  = useState(null);
  const [isActive,  setIsActive]  = useState(false);
  const [testFiles, setTestFiles]             = useState([]);
  const [showTestMenu, setShowTestMenu]       = useState(false);
  const [selectedTestFile, setSelectedTestFile] = useState(null);
  const [alertVisible, setAlertVisible]       = useState(false);
  const [alertConfig,  setAlertConfig]        = useState({});

  const resolvedTag = CATEGORY_TAGS[category] || tag || null;

  const codeRef   = useRef(initialCode);
  const editorRef = useRef(null);

  const handleEditorMount = (editor) => {
    editorRef.current = editor;
    onEditorMount?.(editor);
    const updateHeight = () => {
      const lineCount = editor.getModel()?.getLineCount() || 1;
      const height = Math.max(lineCount * 20 + 20, 60);
      editor.layout({ height, width: editor.getLayoutInfo().width });
    };
    editor.onDidChangeModelContent(updateHeight);
    updateHeight();
  };

  const pushCodeToEditor = (newCode) => {
    codeRef.current = newCode;
    onCodeChange(id, newCode);
    if (editorRef.current) {
      const model = editorRef.current.getModel();
      if (model) model.setValue(newCode);
    }
  };

  const executeRun = async (currentCode) => {
    const allCodes = window.__notebookCellCodes || [];
    const currentIndex = allCodes.findIndex(c => c.id === id);
    if (currentIndex > 0) {
      const previousCodes = allCodes.slice(0, currentIndex).map(c => c.code);
      const validationErrors = validatePinInputs(currentCode, previousCodes, currentIndex);
      if (validationErrors.length > 0) {
        setOutput({ stdout: '', plot: null, error: validationErrors.join('\n') });
        return;
      }
    }
    setIsRunning(true);
    setOutput(null);
    try {
      const result = await runCode(currentCode);
      setOutput(result);
      setRunCount(prev => (prev ?? 0) + 1);
      onCellOutput?.(id, componentId, result);
      onSaveCell?.(id, currentCode, result);
    } finally {
      setIsRunning(false);
    }
  };

  const handleRun = useCallback(async () => {
    if (isRunning || pyLoading) return;
    executeRun(codeRef.current);
  }, [isRunning, pyLoading]);

  const handleSave = () => {
    onSaveCell?.(id, codeRef.current);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const filePathMatch = codeRef.current.match(/FILE_PATH\s*=\s*['"]([^'"]+)['"]/);
    const configuredPath = filePathMatch ? filePathMatch[1] : null;
    const uploadedName = file.name;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const content = new Uint8Array(evt.target.result);
      pyodide.FS.writeFile(`/home/pyodide/${uploadedName}`, content);
      const updatedCode = codeRef.current.replace(
        /FILE_PATH\s*=\s*['"][^'"]*['"]/,
        `FILE_PATH = '/home/pyodide/${uploadedName}'`
      );
      pushCodeToEditor(updatedCode);
      onSaveCell?.(id, updatedCode);
      setSelectedTestFile(uploadedName);
    };

    if (configuredPath && !configuredPath.endsWith(uploadedName)) {
      setAlertConfig({
        title: 'File Path Conflict',
        icon: '⚠️',
        message: `Configured path: ${configuredPath}\nUploaded file: ${uploadedName}\n\nUpdate FILE_PATH to use "${uploadedName}"?`,
        onConfirm: () => {
          setAlertVisible(false);
          reader.readAsArrayBuffer(file);
        }
      });
      setAlertVisible(true);
      return;
    }

    reader.readAsArrayBuffer(file);
  };

  const handleTestFileSelect = async (filename) => {
    setShowTestMenu(false);

    const filePathMatch = codeRef.current.match(/FILE_PATH\s*=\s*['"]([^'"]+)['"]/);
    const configuredPath = filePathMatch ? filePathMatch[1] : null;

    if (configuredPath && !configuredPath.endsWith(filename)) {
      setAlertConfig({
        title: 'File Path Conflict',
        icon: '⚠️',
        message: `Current path: ${configuredPath}\nSelected file: ${filename}\n\nUpdate FILE_PATH to use "${filename}"?`,
        onConfirm: async () => {
          setAlertVisible(false);
          const res = await fetch(`/aitestdata/${filename}`);
          const text = await res.text();
          const encoder = new TextEncoder();
          pyodide.FS.writeFile(`/home/pyodide/${filename}`, encoder.encode(text));
          const updatedCode = codeRef.current.replace(
            /FILE_PATH\s*=\s*['"][^'"]*['"]/,
            `FILE_PATH = '/home/pyodide/${filename}'`
          );
          pushCodeToEditor(updatedCode);
          onSaveCell?.(id, updatedCode);
          setSelectedTestFile(filename);
        }
      });
      setAlertVisible(true);
      return;
    }

    const res = await fetch(`/aitestdata/${filename}`);
    const text = await res.text();
    const encoder = new TextEncoder();
    pyodide.FS.writeFile(`/home/pyodide/${filename}`, encoder.encode(text));
    const updatedCode = codeRef.current.replace(
      /FILE_PATH\s*=\s*['"][^'"]*['"]/,
      `FILE_PATH = '/home/pyodide/${filename}'`
    );
    pushCodeToEditor(updatedCode);
    onSaveCell?.(id, updatedCode);
    setSelectedTestFile(filename);
  };

  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      handleRun();
    }
  };

  const handleTestDataClick = async () => {
    if (!showTestMenu) {
      try {
        const indexFile = componentId === 'AI_DATA_IMAGES'
          ? '/aitestdata/images-index.json'
          : '/aitestdata/index.json';
        const res = await fetch(indexFile);
        const files = await res.json();
        setTestFiles(files);
      } catch {
        setTestFiles(componentId === 'AI_DATA_IMAGES'
          ? ['cat.jpg', 'dog.jpg']
          : ['iris_test.csv', 'housing.csv', 'titanic.csv']);
      }
    }
    setShowTestMenu(prev => !prev);
  };

  const handleTestImageSelect = async (filename) => {
    setShowTestMenu(false);
    const res = await fetch(`/aitestdata/images/${filename}`);
    const buf = await res.arrayBuffer();
    pyodide.FS.writeFile(`/home/pyodide/${filename}`, new Uint8Array(buf));
    const updatedCode = codeRef.current.replace(
      /IMAGE_PATH\s*=\s*['"][^'"]*['"]/,
      `IMAGE_PATH = '/home/pyodide/${filename}'`
    );
    pushCodeToEditor(updatedCode);
    onSaveCell?.(id, updatedCode);
    setSelectedTestFile(filename);
  };

  const hasOutput = output && (output.stdout || output.plot || output.error);

  return (
    <div
      onFocus={() => setIsActive(true)}
      onBlur={() => setIsActive(false)}
      className={`nb-cell ${isActive ? 'nb-cell--active' : ''}`}
    >
      {/* ── Cell toolbar ─────────────────────────────────────────────────── */}
      <div className="nb-toolbar">
        <GripVertical size={14} className="nb-toolbar__grip" />

        <span className="nb-toolbar__run-count">
          {isRunning ? '[*]' : runCount !== null ? `[${runCount}]` : '[ ]'}
        </span>

        {resolvedTag && (
          <span
            className="nb-badge"
            style={{ background: resolvedTag.bg, color: resolvedTag.color }}
          >
            {resolvedTag.label}
          </span>
        )}

        <span className="nb-label">
          {resolvedTag && label ? label : label || 'Python'}
        </span>

        <div className="nb-toolbar__actions">

          {/* File upload — only for data source cells */}
          {(componentId === 'AI_DATA_CSV' || componentId === 'AI_DATA_IMAGES') && (
            <>
              {/* Test data dropdown */}
              <div className="nb-test-data-wrapper">
                <button onClick={handleTestDataClick} className="nb-btn--test-data">
                  {selectedTestFile ? `📄 ${selectedTestFile} ▾` : '💾 Test Data ▾'}
                </button>
                {showTestMenu && testFiles.length > 0 && (
                  <div className="nb-test-menu">
                    {testFiles.map(f => (
                      <div
                        key={f}
                        className="nb-test-menu__item"
                        onClick={() =>
                          componentId === 'AI_DATA_IMAGES'
                            ? handleTestImageSelect(f)
                            : handleTestFileSelect(f)
                        }
                      >
                        📄 {f}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Upload CSV/Image */}
              <label className="nb-btn--upload">
                {componentId === 'AI_DATA_IMAGES' ? '🖼️ Upload Image' : '📂 Upload CSV'}
                <input
                  type="file"
                  accept={componentId === 'AI_DATA_IMAGES' ? 'image/*' : '.csv'}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </label>
            </>
          )}

          {/* Run cell */}
          <button
            onClick={handleRun}
            disabled={isRunning || pyLoading}
            title="Run cell (Shift+Enter)"
            className="nb-btn--run"
          >
            <Play size={12} />
            {isRunning ? 'Running...' : 'Run'}
          </button>

          {/* Save */}
          <button onClick={handleSave} title="Save cell code" className="nb-btn--icon nb-btn--save">
            💾
          </button>

          {/* Regenerate */}
          <button
            onClick={() => onRegenerateCell?.(id)}
            title="Regenerate cell code from pipeline configuration"
            className="nb-btn--icon nb-btn--regenerate"
          >
            🔄
          </button>

          {/* Insert cell below */}
          <button
            onClick={() => onInsertBelow(id)}
            title="Insert cell below"
            className="nb-btn--icon nb-btn--insert"
          >
            <Plus size={12} />
          </button>

          {/* Delete cell */}
          <button
            onClick={() => onDelete(id)}
            title="Delete cell"
            className="nb-btn--icon nb-btn--delete"
          >
            <Trash2 size={12} />
          </button>

        </div>
      </div>

      {/* ── Monaco Editor ─────────────────────────────────────────────────── */}
      <div className="nb-editor" onKeyDown={handleKeyDown}>
        <Editor
          defaultLanguage="python"
          defaultValue={initialCode}
          theme="vs-dark"
          onMount={handleEditorMount}
          onChange={(value) => {
            codeRef.current = value || '';
            onCodeChange(id, value || '');
          }}
          options={{
            minimap:              { enabled: false },
            scrollBeyondLastLine: false,
            fontSize:             13,
            lineHeight:           20,
            padding:              { top: 10, bottom: 10 },
            lineNumbers:          'on',
            folding:              false,
            renderLineHighlight:  'line',
            scrollbar:            { vertical: 'hidden', horizontal: 'hidden' },
            overviewRulerLanes:   0,
            automaticLayout:      true,
            tabSize:              4,
            wordWrap:             'on',
          }}
        />
      </div>

      {/* ── Running indicator ─────────────────────────────────────────────── */}
      {isRunning && (
        <div className="nb-running">
          <span className="nb-running__dot" />
          <span className="nb-running__dot" />
          <span className="nb-running__dot" />
          <span className="nb-running__label">Executing…</span>
        </div>
      )}

      {/* ── Output area ───────────────────────────────────────────────────── */}
      {(hasOutput || (isRunning === false && output !== null)) && (
        <div className="nb-output">

          {/* Expected output label pills */}
          {simOutputs.length > 0 && (
            <div className="nb-output__labels">
              {simOutputs.map((out, i) => (
                <span key={i} className="nb-output__label-pill">
                  {out.name}
                  {out.unit    ? ` (${out.unit})`    : ''}
                  {out.dataType ? ` · ${out.dataType}` : ''}
                </span>
              ))}
            </div>
          )}

          {/* Actual output */}
          <div className={`nb-output__content ${output?.error ? 'nb-output__content--error' : ''}`}>

            {output?.error && (
              <pre className="nb-output__error">{output.error}</pre>
            )}

            {output?.stdout && !output?.error && (
              <pre className="nb-output__stdout">{output.stdout}</pre>
            )}

            {output?.plot && (
              <img
                src={`data:image/png;base64,${output.plot}`}
                alt="Plot output"
                className={`nb-output__plot ${output?.stdout ? 'nb-output__plot--with-stdout' : ''}`}
              />
            )}

            {!output?.error && !output?.stdout && !output?.plot && output !== null && (
              <span className="nb-output__empty">(no output)</span>
            )}

          </div>
        </div>
      )}

      <CustomAlert
        visible={alertVisible}
        title={alertConfig.title}
        icon={alertConfig.icon}
        message={alertConfig.message}
        onConfirm={alertConfig.onConfirm}
        onCancel={() => setAlertVisible(false)}
      />

    </div>
  );
};

export default NotebookCell;
